/**
 * Admin Beta Feedback Dashboard
 * 
 * Module 10B: Beta Feedback visibility for founders.
 * Shows friction tags, severity trends, and recent summaries.
 * No PII, no raw transcripts (aggregated by default).
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { AppLayout } from "./DashboardPage";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { 
  MessageSquare,
  TrendingUp,
  AlertCircle,
  Loader2,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Meh,
  Filter
} from "lucide-react";
import api from "../lib/api";

// Friction tag color map
const TAG_COLORS = {
  navigation: "bg-blue-100 text-blue-700",
  confusing_copy: "bg-amber-100 text-amber-700",
  missing_feature: "bg-purple-100 text-purple-700",
  bug: "bg-red-100 text-red-700",
  performance: "bg-orange-100 text-orange-700",
  emotional_trigger: "bg-rose-100 text-rose-700",
  other: "bg-slate-100 text-slate-700",
};

// Emotion icons
const EMOTION_ICONS = {
  calm: { icon: ThumbsUp, color: "text-emerald-600" },
  clear: { icon: ThumbsUp, color: "text-blue-600" },
  confusing: { icon: Meh, color: "text-amber-600" },
  heavy: { icon: ThumbsDown, color: "text-rose-600" },
  not_sure: { icon: Meh, color: "text-slate-500" },
};

// Severity badge colors
const SEVERITY_COLORS = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-amber-100 text-amber-700",
  high: "bg-red-100 text-red-700",
};

// Metric Card
function MetricCard({ title, value, subtitle, trend }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <p className="text-sm text-slate-500 mb-1">{title}</p>
      <div className="flex items-baseline gap-2">
        <p className="text-2xl font-semibold">{value}</p>
        {trend && (
          <span className={`text-sm ${trend > 0 ? 'text-emerald-600' : 'text-slate-500'}`}>
            {trend > 0 ? '+' : ''}{trend}%
          </span>
        )}
      </div>
      {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
    </div>
  );
}

// Friction Tag Distribution
function FrictionTagChart({ tags }) {
  if (!tags || tags.length === 0) {
    return <p className="text-sm text-slate-500">No friction tags yet</p>;
  }

  const maxCount = Math.max(...tags.map(t => t.count));

  return (
    <div className="space-y-2">
      {tags.map(({ tag, count }) => (
        <div key={tag} className="flex items-center gap-3">
          <span className="text-sm w-32 truncate capitalize">{tag.replace(/_/g, ' ')}</span>
          <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden">
            <div 
              className={`h-full ${TAG_COLORS[tag] || 'bg-slate-200'} rounded-full transition-all`}
              style={{ width: `${(count / maxCount) * 100}%` }}
            />
          </div>
          <span className="text-sm text-slate-500 w-8 text-right">{count}</span>
        </div>
      ))}
    </div>
  );
}

// Recent Summary Card
function SummaryCard({ feedback }) {
  const EmotionIcon = EMOTION_ICONS[feedback.emotion]?.icon || Meh;
  const emotionColor = EMOTION_ICONS[feedback.emotion]?.color || "text-slate-500";
  
  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4" data-testid="feedback-summary-card">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <EmotionIcon className={`w-4 h-4 ${emotionColor}`} />
          <span className="text-xs text-slate-500 capitalize">{feedback.page_key}</span>
          <span className="text-xs text-slate-400">·</span>
          <span className="text-xs text-slate-400">
            {new Date(feedback.created_at).toLocaleDateString()}
          </span>
        </div>
        {feedback.ai_summary?.severity && (
          <Badge className={`${SEVERITY_COLORS[feedback.ai_summary.severity]} text-xs`}>
            {feedback.ai_summary.severity}
          </Badge>
        )}
      </div>
      
      <p className="text-sm text-slate-700 mb-2">
        {feedback.ai_summary?.summary || "No summary available"}
      </p>
      
      {feedback.ai_summary?.friction_tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {feedback.ai_summary.friction_tags.map(tag => (
            <Badge key={tag} className={`${TAG_COLORS[tag] || TAG_COLORS.other} text-xs`}>
              {tag.replace(/_/g, ' ')}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

// Main Page
export default function AdminFeedbackPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  // Check admin access
  useEffect(() => {
    const adminEmails = ["erana@arawealth.app", "demo@arawealth.com", "admin@arawealth.com"];
    if (user && !adminEmails.includes(user.email)) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  // Fetch feedback summary
  useEffect(() => {
    fetchSummary();
  }, [period]);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.feedback.getAdminSummary(period);
      
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      } else if (response.status === 403) {
        setError("Admin access required");
      } else if (response.status === 503) {
        setError("Feedback admin export is disabled");
      } else {
        setError("Failed to load feedback data");
      }
    } catch (err) {
      console.error("Feedback fetch error:", err);
      setError("Failed to load feedback data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="admin-feedback-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">Beta Feedback</h1>
            <p className="text-sm text-slate-500">
              Aggregated feedback insights (no PII, no raw transcripts)
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            <select 
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white"
              data-testid="period-select"
            >
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            
            <Button 
              variant="outline" 
              size="icon"
              onClick={fetchSummary}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            <AlertCircle className="w-5 h-5 inline mr-2" />
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && !summary && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        )}

        {/* Content */}
        {summary && !error && (
          <>
            {/* Overview Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard
                title="Total Feedback"
                value={summary.total || 0}
                subtitle={`Past ${period} days`}
              />
              <MetricCard
                title="Voice Input"
                value={summary.by_input_mode?.voice || 0}
                subtitle={`${Math.round((summary.by_input_mode?.voice || 0) / Math.max(summary.total, 1) * 100)}% of total`}
              />
              <MetricCard
                title="High Severity"
                value={summary.by_severity?.high || 0}
                subtitle="Need attention"
              />
              <MetricCard
                title="Confusing/Heavy"
                value={(summary.by_emotion?.confusing || 0) + (summary.by_emotion?.heavy || 0)}
                subtitle="Friction signals"
              />
            </div>

            {/* Two Column Layout */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Friction Tags */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="text-sm font-medium mb-4">Top Friction Tags</h2>
                <FrictionTagChart tags={summary.top_friction_tags || []} />
              </div>

              {/* High Friction Pages */}
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h2 className="text-sm font-medium mb-4">Pages with Most Friction</h2>
                {summary.high_friction_pages?.length > 0 ? (
                  <div className="space-y-3">
                    {summary.high_friction_pages.map(({ page, count }) => (
                      <div key={page} className="flex items-center justify-between">
                        <span className="text-sm capitalize">{page.replace(/_/g, ' ')}</span>
                        <Badge variant="outline">{count} reports</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">No high-friction pages identified</p>
                )}
              </div>
            </div>

            {/* Emotion Distribution */}
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h2 className="text-sm font-medium mb-4">Emotion Distribution</h2>
              <div className="flex flex-wrap gap-3">
                {Object.entries(summary.by_emotion || {}).map(([emotion, count]) => {
                  const Icon = EMOTION_ICONS[emotion]?.icon || Meh;
                  const color = EMOTION_ICONS[emotion]?.color || "text-slate-500";
                  return (
                    <div key={emotion} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg">
                      <Icon className={`w-4 h-4 ${color}`} />
                      <span className="text-sm capitalize">{emotion.replace(/_/g, ' ')}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Recent Summaries */}
            <div>
              <h2 className="text-sm font-medium mb-4">Recent Feedback Summaries</h2>
              {summary.recent_summaries?.length > 0 ? (
                <div className="grid md:grid-cols-2 gap-4">
                  {summary.recent_summaries.map((fb) => (
                    <SummaryCard key={fb.feedback_id} feedback={fb} />
                  ))}
                </div>
              ) : (
                <div className="bg-slate-50 rounded-lg p-8 text-center text-slate-500">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No feedback summaries yet</p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="text-xs text-center text-slate-400 pt-4">
              Data aggregated from {summary.total || 0} feedback submissions
              {" · "}
              Privacy-safe: No PII, no raw transcripts displayed
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
